import type { File } from "~/models/polyschema/File"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { InterfaceConversation } from "~/classes/conversation/InterfaceConversation"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { InterfaceFile } from "~/classes/file/InterfaceFile"
import { InterfacePanelState } from "~/classes/generated/interface"
import { sendFilePropertiesRequests } from "~/classes/handlers/file/properties"
import { sendSharedFolderFindRequests } from "~/classes/handlers/sharedFolder/find"
import { InterfaceSearch } from "~/classes/search/InterfaceSearch"

// These interfaces need to be serializable to JSON in order to pack up in the
// window history state
interface NavigationCollectionFolderState {
  navType: "collection-folder"
  root: File // the path in our nav is this file's path
  openFolders?: Record<string, boolean>
  sharedFolderHome?: File
}
interface NavigationCollectionSearchState {
  navType: "collection-search"
  search: Search
  // Whether this is a newly created search or an existing search
  fresh: boolean
  // TODO: In a search, could you also have tree/column view!
  openFolders: Record<string, boolean>
}
interface NavigationFilePreviewState {
  navType: "preview-file"
  file: File // the path in our nav is this file's path
  sharedFolderHome?: File
}

interface NavigationConversationState {
  navType: "conversation"
  conversationId: string
  // TODO: scroll position saved here
}

interface NavigationSettingsState {
  navType: "settings"
  // TODO: scroll position saved here
}
type NavigationPageState = NavigationCollectionFolderState | NavigationCollectionSearchState | NavigationFilePreviewState | NavigationConversationState | NavigationSettingsState

class Navigation extends GlobalComposable<{
  appLoadingPromise?: Promise<void>
  prior?: NavigationPageState
  current?: NavigationPageState
  next?: NavigationPageState
}> {
  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("afterNavigatedToFolder", this.updateNavigationPathListeners)
    addInterfaceEventListener("afterNavigatedToNonFolderFile", this.updateNavigationPathListeners)
    addInterfaceEventListener("afterNavigatedToSearch", this.updateNavigationPathListeners)
    addInterfaceEventListener("afterNavigatedToConversation", this.updateNavigationPathListeners)

    if (window)
      window.addEventListener("popstate", this.onAfterPopState)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("afterNavigatedToFolder", this.updateNavigationPathListeners)
    removeInterfaceEventListener("afterNavigatedToNonFolderFile", this.updateNavigationPathListeners)
    removeInterfaceEventListener("afterNavigatedToSearch", this.updateNavigationPathListeners)
    removeInterfaceEventListener("afterNavigatedToConversation", this.updateNavigationPathListeners)

    if (window)
      window.removeEventListener("popstate", this.onAfterPopState)
  }

  currentFilePreview = computed(() => {
    if (this.state.value.current?.navType !== "preview-file")
      return
    const file = InterfaceFile.from(this.state.value.current.file)
    if (!file || file.isBlank())
      return
    return file
  })

  currentSearch = computed(() => {
    if (this.state.value.current?.navType !== "collection-search")
      return
    const search = InterfaceSearch.from(this.state.value.current.search)
    if (!search || search.isBlank())
      return
    return search
  })

  currentSearchIsFresh = computed(() => {
    if (this.state.value.current?.navType !== "collection-search")
      return false
    return this.state.value.current.fresh
  })

  currentSearchResults = computed(() => {
    if (this.state.value.current?.navType !== "collection-search")
      return {}
    const search = InterfaceSearch.from(this.state.value.current.search)
    if (!search || search.isBlank())
      return {}
    return search.searchResults()
  })

  currentRootFolder = computed(() => {
    if (this.state.value.current?.navType !== "collection-folder")
      return
    const folder = InterfaceFile.from(this.state.value.current.root)
    if (!folder || folder.isBlank())
      return
    return folder
  })

  currentOpenFolders = computed(() => {
    if (this.state.value.current?.navType === "collection-folder")
      return this.state.value.current.openFolders ?? {}
    else if (this.state.value.current?.navType === "collection-search")
      return this.state.value.current.openFolders ?? {}
    return {}
  })

  // TODO: is-blank edge case?
  currentSharedFolderHome = computed(() => {
    let info
    if (this.state.value.current?.navType === "collection-folder")
      info = InterfaceFile.from(this.state.value.current.sharedFolderHome)
    if (this.state.value.current?.navType === "preview-file")
      info = InterfaceFile.from(this.state.value.current.sharedFolderHome)
    return info
  })

  currentPath = computed(() => {
    const root = this.currentFilePreview.value ?? this.currentRootFolder.value
    if (!root)
      return
    const path = root.path() ?? []
    return [root, ...path]
  })

  currentNavConversationId = computed(() => {
    if (this.state.value.current?.navType !== "conversation")
      return
    return this.state.value.current.conversationId
  })

  initializeWorkers = async () => {
    const { waitForCacheWorkerToBeSetUp } = useCache()
    const { initSandboxWorker } = useSandbox()
    const { initTauriHandlers } = useTauri()
    await Promise.all([waitForCacheWorkerToBeSetUp(), initSandboxWorker(), initTauriHandlers()])
  }

  /**
   * Mounts the app once. If we have multiple callers that call this, then they will
   * receive a callback function that resolves when the function has
   * completed. If two callbacks call this at once with different auth levels,
   * that is a runtime error.
   * NOTE: should this go somewhere else?
   */
  waitUntilAppLoaded = async () => {
    if (!this.state.value.appLoadingPromise)
      this.state.value.appLoadingPromise = this._waitUntilAppLoaded()
    return await this.state.value.appLoadingPromise
  }

  _waitUntilAppLoaded = async () => {
    const { cacheState, sendCacheGuestLogin, sendCacheLogin } = useCache()
    const { state: myself, requestAllSelfDetails } = useSelf()

    if (cacheState.value.status === "uninitialized")
      await this.initializeWorkers()

    if (cacheState.value.status === "uninitialized") // still uninitialized
      throw new Error("Cache was not able to start up")

    if (cacheState.value.anonId && cacheState.value.status === "offlineAnon") {
      // so that we receive signal if we turn out to still be offline
      cacheState.value.status = "connectingAnon"
      const { promise, resolve } = Promise.withResolvers<any>()
      watchOnce(() => cacheState.value.status, resolve)
      sendCacheGuestLogin(cacheState.value.anonId)
      await promise
      // @ts-expect-error TODO: this is a bug in the types
      if (cacheState.value.status === "pendingAnon")
        return this.warn("Network offline, will start in offline mode")
    }

    if (cacheState.value.userId && cacheState.value.status === "offlineAuth") {
      // so that we receive signal if we turn out to still be offline
      cacheState.value.status = "connectingAuth"
      const { promise, resolve } = Promise.withResolvers<any>()
      watchOnce(() => cacheState.value.status, resolve)
      sendCacheLogin(cacheState.value.userId)
      await promise
      // @ts-expect-error TODO: this is a bug in the types
      if (cacheState.value.status === "pendingAuth") {
        this.warn("Network offline, will start in offline mode")
      }
    }

    if (["readyAuth", "pendingAuth"].includes(cacheState.value.status) && !myself.value.home) {
      const { promise, resolve } = Promise.withResolvers<any>()
      watchOnce(() => myself.value.home, resolve)
      requestAllSelfDetails()
      await promise
      if (!myself.value.home)
        throw new Error("Home is still not set somehow")
    }
  }

  isAppLoaded = () => {
    const { cacheState } = useCache()
    const { state: myself } = useSelf()
    return !!cacheState.value.anonId || !!myself.value.user
  }

  isOnSearchPage = () => {
    return this.state.value.current?.navType === "collection-search"
  }

  isOnPreviewPage = () => {
    return this.state.value.current?.navType === "preview-file"
  }

  resetFileSystemNavigationState = () => {
    this.state.value = {}
  }

  setCurrentNavigationState = (current?: NavigationPageState) => {
    this.state.value.current = current
    this.setHistoryStateFromNavigationState()
  }

  setNextNavigationState = (next?: NavigationPageState) => {
    this.state.value.next = next
  }

  clearNextNavigationState = () => {
    this.state.value.next = undefined
  }

  setPriorNavigationState = (prior?: NavigationPageState) => {
    this.state.value.prior = prior
  }

  setNavigationStateRootFolder = ({
    root,
    sharedFolderHome,
  }: {
    root: InterfaceFile
    sharedFolderHome?: InterfaceFile
  }) => {
    if (this.state.value.current?.navType === "collection-folder") {
      this.state.value.current.root = root.inner()
      this.state.value.current.sharedFolderHome = sharedFolderHome?.inner()
    }
    else {
      this.state.value.current = {
        navType: "collection-folder",
        root: root.inner(),
        openFolders: { [root.fileId]: true },
        sharedFolderHome: sharedFolderHome?.inner(),
      }
    }
    this.setHistoryStateFromNavigationState()
  }

  setNavigationStateToFilePreview = ({
    file,
    sharedFolderHome,
  }: {
    file: InterfaceFile
    sharedFolderHome?: InterfaceFile
  }) => {
    // carry over the shared folder info if we know we are in the same shared folder
    // as the prior folder.
    if (!sharedFolderHome) {
      sharedFolderHome = this.currentSharedFolderHome.value
      const rootSharedFolderId = file.rootSharedFolderId()
      if (!rootSharedFolderId || rootSharedFolderId !== sharedFolderHome?.rootSharedFolderId())
        sharedFolderHome = undefined
    }
    this.state.value.current = {
      navType: "preview-file",
      file: file.inner(),
      sharedFolderHome: sharedFolderHome?.inner(),
    }
    this.setHistoryStateFromNavigationState()
  }

  setNavigationStateToSearch = ({
    search,
    fresh,
  }: {
    search: InterfaceSearch
    fresh: boolean
  }) => {
    if (this.state.value.current?.navType === "collection-search") {
      this.state.value.current.search = search.inner()
    }
    else {
      this.state.value.current = {
        navType: "collection-search",
        search: search.inner(),
        fresh,
        openFolders: {},
      }
    }
    this.setHistoryStateFromNavigationState()
  }

  /**
   * If the folder has a shared folder as its root, then we need to add
   * it to the navigation state. Use the shared folder from our shared
   * folder state if we have one, otherwise use the folder's own. If the file has
   * no valid shared folder, return undefined
   */

  getSharedFolderHomeFromStateOrPath = (file: InterfaceFile) => {
    const { state: sharedFolderState } = useSharedFolders()
    const sharedFolderId = file.rootSharedFolderId()
    if (isDefined(sharedFolderId)) {
      const home = sharedFolderState.value.all[sharedFolderId]?.home
      if (isDefined(home))
        return home
    }
    const root = file.rootFolder()
    if (root?.isSharedFolderHome())
      return root
    else if (root?.isSharedFolderArchive())
      this.warn("Fresh navigation to shared folder archive not fully implemented")
  }

  navigateToOnboarding = async () => {
    return navigateTo("/onboarding")
  }

  navigateToConversation = async (conversation: InterfaceConversation) => {
    const { addOrUpdateConversation: addConversation, setupCurrentConversation } = useConversations()

    addConversation(conversation)
    setupCurrentConversation(conversation)

    this.setNextNavigationState({
      navType: "conversation",
      conversationId: conversation.conversationId,
    })
    return await navigateTo(`/conversation/id/${asShortId(conversation.conversationId)}`)
  }

  /**
   * Creates a brand new conversation and navigates to it
   */
  navigateToNewConversation = async () => {
    const { createNewConversation, waitForConversationsToInitialize } = useConversations()
    await waitForConversationsToInitialize()
    const conversation = await createNewConversation()
    return this.navigateToConversation(conversation)
  }

  /**
   * For navigating to a "new" conversation, but will reuse an old one if you have already created a new
   * one, so that we have at most one new conversation going.
   */
  navigateToLatestOrNewConversation = async () => {
    const { getLatestIfEmptyOrCreateConversation, waitForConversationsToInitialize } = useConversations()
    await waitForConversationsToInitialize()
    const conversation = await getLatestIfEmptyOrCreateConversation()
    return this.navigateToConversation(conversation)
  }

  navigateToSelfHome = async () => {
    const { state: myself } = useSelf()
    if (!myself.value.home)
      throw new Error("Cannot navigate to home")

    this.setNextNavigationState({
      navType: "collection-folder",
      root: myself.value.home.inner(),
      openFolders: { [myself.value.home.fileId]: true },
    })
    return await this.navigateToFolder(myself.value.home)
  }

  navigateToSelfArchive = async () => {
    const { state: myself } = useSelf()
    if (!myself.value.archive)
      throw new Error("Cannot navigate to archive")

    this.setNextNavigationState({
      navType: "collection-folder",
      root: myself.value.archive.inner(),
      openFolders: { [myself.value.archive.fileId]: true },
    })
    return await this.navigateToFolder(myself.value.archive)
  }

  navigateToFolder = async (folder: InterfaceFile) => {
    const { state: myself } = useSelf()

    const folderId = folder.fileId

    // preemptively set the navigation state
    this.setNextNavigationState({
      navType: "collection-folder",
      root: folder.inner(),
      openFolders: { [folder.fileId]: true },
      sharedFolderHome: this.getSharedFolderHomeFromStateOrPath(folder)?.inner(),
    })

    if (myself.value.home && folderId === myself.value.home.fileId)
      return await navigateTo(`/home`)
    else if (myself.value.archive && folderId === myself.value.archive.fileId)
      return await navigateTo(`/archive`)
    else
      return await navigateTo(`/folder/id/${asShortId(folderId)}`)
  }

  navigateToFile = async (file: InterfaceFile) => {
    if (file.isBlank())
      return await navigateTo(`/file/id/${asShortId(file.fileId)}`)
    if (file.isFolder())
      return await this.navigateToFolder(file)
    if (file.isLink())
      return await this.navigateToLink(file)

    this.setNextNavigationState({
      navType: "preview-file",
      file: file.inner(),
      sharedFolderHome: this.getSharedFolderHomeFromStateOrPath(file)?.inner(),
    })

    return await navigateTo(`/file/id/${asShortId(file.fileId)}`)
  }

  navigateToLink = async (link: InterfaceFile) => {
    const { isAuthenticated } = useSelf()
    const { setFileAccessedIfAllowed } = useFileSystem()
    const url = link.linkDestinationUrl()
    if (!url)
      return this.error("File doesn't have a destination", link)

    if (isDesktop()) {
      const { launchWithDefaultApplication } = useTauri()
      return launchWithDefaultApplication(url)
    }

    if (isAuthenticated.value)
      setFileAccessedIfAllowed(link)

    window.open(url, "_blank")
  }

  setHistoryStateFromNavigationState = () => {
    if (!this.state.value.current)
      throw new Error("No current navigation state!")

    // Vue router has some stuff it saves in history so we need to retain that data, while
    // adding our own fields to the history.
    window.history.replaceState({
      ...window.history.state,
      navigationState: JSON.stringify(this.state.value.current),
    }, "")
  }

  openFolderInNewTab = async (folder: InterfaceFile) => {
    return await navigateTo(
      `/folder/id/${asShortId(folder.fileId)}`,
      { open: { target: "_blank" } },
    )
  }

  openFileInNewTab = async (file: InterfaceFile) => {
    if (file.isFolder())
      return await this.openFolderInNewTab(file)
    return await navigateTo(
      `/file/id/${asShortId(file.fileId)}`,
      { open: { target: "_blank" } },
    )
  }

  openSearchInNewTab = async (search: InterfaceSearch) => {
    return await navigateTo(
      `/search/id/${asShortId(search.searchId())}`,
      { open: { target: "_blank" } },
    )
  }

  /**
   * After a popstate event, we want to check if the state contains our own state object, and
   * if so, we load it ourselves as the "next", which will get loaded by the navigation
   * callback.
   */
  onAfterPopState = (event: PopStateEvent) => {
    this.debug("Popped history state", event)

    if (!event.state?.navigationState)
      return

    try {
      const historyState: NavigationPageState = JSON.parse(event.state.navigationState)
      if (!historyState.navType || !["collection-folder", "collection-search", "preview-file", "agent"].includes(historyState.navType))
        throw new Error("No navigation type to deserialize or wrong type")
      this.state.value.next = historyState
    }
    catch (error) {
      this.error("Could not deserialize json navigation state", error)
      return
    }
  }

  onAfterNavigatedToConversation = async (conversationId: string) => {
    const { switchMainView } = useMainView()
    const { clearSelected } = useSelection()
    const { emitInterfaceEvent } = useEvents()
    const { conversation } = useConversations()
    const { isShowingPanel, showPanel } = usePanel()

    let nextConversation: InterfaceConversation | undefined
    if (this.state.value.next?.navType === "conversation")
      nextConversation = conversation(conversationId)
    if (!isDefined(nextConversation) || !this.state.value.current) {
      // If we have no prior conversation, i.e. we just landed on the page, we
      // need to request the conversation properties and err if the user doesn't
      // have access to this conversation id.

      nextConversation = InterfaceConversation.blank(conversationId)
      this.setCurrentNavigationState({
        navType: "conversation",
        conversationId,
      })

      // Check if the user has access to this conversation
      nextConversation = await this.authenticateFreshConversation(conversationId)
      if (!nextConversation)
        return
    }
    else {
      // Handle edge cases
      if (nextConversation?.conversationId !== conversationId)
        throw new Error("Navigation state contains the wrong convo, it was not pre-loaded")
      if (conversationId === this.currentNavConversationId.value)
        throw new Error("Navigating to existing conversation")
      this.setPriorNavigationState(this.state.value.current)
      this.setCurrentNavigationState(this.state.value.next)
      this.clearNextNavigationState()
    }

    // if the panel is not compatible with agent view, switch it
    if (isShowingPanel(InterfacePanelState.Agent) || isShowingPanel(InterfacePanelState.Waterfall))
      showPanel(InterfacePanelState.Chats)

    switchMainView("agent")
    clearSelected() // TODO: needed?
    emitInterfaceEvent("afterNavigatedToConversation", nextConversation)
  }

  onAfterNavigatedToFolder = async (fileId: string) => {
    const { emitInterfaceEvent, addInterfaceEventListener } = useEvents()
    const { clearSelected } = useSelection()
    const { switchMainView } = useMainView()
    const { switchCollectionType } = useCollectionView()
    const { isOffline } = useCache()

    // if we have no prior file here, i.e. we just landed on the page, since if we were
    // doing an internal navigation we would have set the path file already (see
    // "navigateToFolder")

    let nextFile: InterfaceFile | undefined
    if (this.state.value.next?.navType === "collection-folder")
      nextFile = InterfaceFile.from(this.state.value.next.root)
    if (!nextFile || !this.state.value.current) {
      // If there is nothing in the "next" or "current", then we need to request the file
      // from the server/cache and error if the response was an error (e.g. unauthorized)

      const requestId = generateWorkerRequestId()
      addInterfaceEventListener("filePropertiesResponse", this.onFreshFileProperties, { requestId })
      sendFilePropertiesRequests([{
        requestId,
        fileId,
        selection: InterfaceSelection.minimumPathSelection().inner(),
        pathSelection: InterfaceSelection.minimumPathSelection().inner(),
        offline: isOffline(),
      }])

      nextFile = InterfaceFile.blank(fileId)
      this.setCurrentNavigationState({
        navType: "collection-folder",
        root: InterfaceFile.blank(fileId).inner(),
        openFolders: { [fileId]: true },
      })
    }
    else {
      // Handle edge cases
      if (nextFile?.fileId !== fileId)
        throw new Error("Navigation state contains the wrong file, it was not pre-loaded")
      if (fileId === this.currentRootFolder.value?.fileId)
        throw new Error("Navigating to existing folder")
      this.setPriorNavigationState(this.state.value.current)
      this.setCurrentNavigationState(this.state.value.next)
      this.clearNextNavigationState()
    }

    switchMainView("collection")
    switchCollectionType("folder")
    clearSelected() // TODO: needed?
    emitInterfaceEvent("afterNavigatedToFolder", nextFile)
  }

  onAfterNavigatedToFile = (fileId: string) => {
    const { emitInterfaceEvent, addInterfaceEventListener } = useEvents()
    const { isOffline } = useCache()
    const { clearSelected } = useSelection()
    const { switchMainView } = useMainView()

    let nextFile: InterfaceFile | undefined
    if (this.state.value.next?.navType === "preview-file")
      nextFile = InterfaceFile.from(this.state.value.next.file)
    if (!nextFile || !this.state.value.current) {
      this.setCurrentNavigationState({
        navType: "preview-file",
        file: InterfaceFile.blank(fileId).inner(),
      })
      nextFile = InterfaceFile.blank(fileId)
    }
    else {
      // Handle edge cases
      if (nextFile?.fileId !== fileId)
        throw new Error("Navigation state contains the wrong file, it was not pre-loaded")
      if (fileId === this.currentFilePreview.value?.fileId)
        throw new Error("Navigating to existing folder")
      this.setPriorNavigationState(this.state.value.current)
      this.setCurrentNavigationState(this.state.value.next)
      this.clearNextNavigationState()
    }

    // We retrieve the properties for every file here, just in case we don't have any needed
    // properties for the preview view, though this may be wasteful?
    // TODO: can we ever skip retrieving file properties?
    const requestId = generateWorkerRequestId()
    addInterfaceEventListener("filePropertiesResponse", this.onFreshFileProperties, { requestId })
    sendFilePropertiesRequests([{
      requestId,
      fileId,
      selection: InterfaceSelection.minimumPropertiesViewSelection().inner(),
      pathSelection: InterfaceSelection.minimumPathSelection().inner(),
      offline: isOffline(),
    }])

    switchMainView("preview")
    clearSelected()
    emitInterfaceEvent("afterNavigatedToNonFolderFile", nextFile)
  }

  navigateToSharedFolder = async (home: InterfaceFile) => {
    const handle = home.rootSharedFolderHandle()
    if (!isDefined(handle))
      return this.error("must have a handle at the moment")

    this.setNextNavigationState({
      navType: "collection-folder",
      root: home.inner(),
      sharedFolderHome: home.inner(),
    })

    return await navigateTo(`/shared/${handle.handle}`)
  }

  onAfterNavigatedToSharedFolder = async (handle: string) => {
    const { emitInterfaceEvent, waitForFirstResponse } = useEvents()
    const { clearSelected } = useSelection()
    const { switchMainView } = useMainView()
    const { switchCollectionType } = useCollectionView()
    const { notifyError } = useAlerts()
    const { isAuthenticated } = useCache()

    // if we have no prior file here, i.e. we just landed on the page, since if we were
    // doing an internal navigation we would have set the path file already (see
    // "navigateToFolder")

    let nextFile: InterfaceFile | undefined
    if (this.state.value.next?.navType === "collection-folder")
      nextFile = InterfaceFile.from(this.state.value.next.root)

    if (!nextFile?.rootSharedFolderHandle() || !this.state.value.current) {
      // If there is nothing in the "next" or "current", then we know this is a fresh
      // navigation, and so we set this file as the current, and update the path listeners
      // to listen for this file's properties.

      const requestId = generateWorkerRequestId()
      const promise = waitForFirstResponse("sharedFolderFindResponse", requestId)
      sendSharedFolderFindRequests([{
        requestId,
        handle: { handle },
        offline: false,
      }])
      const response = await promise

      if (response.error || !response.sharedFolderId || !response.home || !response.archive) {
        this.error("Received a navigation error", response)
        notifyError("Cannot access that file", response.error!.details)
        if (isAuthenticated())
          return await this.navigateToSelfHome()
        else {
          return await this.navigateToLoginScreenAndClearCredentials()
        }
      }

      const sharedFolderHome = new InterfaceFile(response.home)

      this.setCurrentNavigationState({
        navType: "collection-folder",
        root: sharedFolderHome.inner(),
        sharedFolderHome: sharedFolderHome.inner(),
      })
      nextFile = sharedFolderHome
    }
    else {
      if (nextFile.fileId === this.currentRootFolder.value?.fileId)
        throw new Error("Navigating to existing folder")
      this.setPriorNavigationState(this.state.value.current)
      this.setCurrentNavigationState(this.state.value.next)
      this.clearNextNavigationState()
    }

    switchMainView("collection")
    switchCollectionType("folder")
    clearSelected() // TODO: needed?
    emitInterfaceEvent("afterNavigatedToFolder", nextFile)
  }

  navigateToLoginScreenAndClearCredentials = async () => {
    const { clearUserData } = useSelf()
    clearUserData()
    await navigateTo("/login")
  }

  navigateToSearch = async (search: InterfaceSearch, fresh: boolean) => {
    this.setNextNavigationState({
      navType: "collection-search",
      search: search.inner(),
      fresh,
      openFolders: {},
    })
    return await navigateTo(`/search/id/${asShortId(search.searchId())}`)
  }

  onAfterNavigatedToSearch = (searchId: string) => {
    const { switchMainView } = useMainView()
    const { switchCollectionType } = useCollectionView()
    const { emitInterfaceEvent } = useEvents()
    const { clearSelected } = useSelection()

    // if we have no prior search here, i.e. we just landed on the page, since if we were
    // doing an internal navigation we would have set the search already (see
    // "navigateToSearch")

    let fresh = false // if we just navigated, it can't be fresh
    let nextSearch: InterfaceSearch | undefined
    if (this.state.value.next?.navType === "collection-search") {
      nextSearch = InterfaceSearch.from(this.state.value.next.search)
      fresh = this.state.value.next.fresh
    }

    if (!nextSearch || !this.state.value.current) {
      nextSearch = InterfaceSearch.blank(searchId)
      this.setCurrentNavigationState({
        navType: "collection-search",
        search: nextSearch.inner(),
        fresh,
        openFolders: {},
      })
    }
    else {
      if (nextSearch.searchId() === this.currentSearch.value?.searchId())
        throw new Error("Navigating to existing search")
      this.setPriorNavigationState(this.state.value.current)
      this.setCurrentNavigationState(this.state.value.next)
      this.clearNextNavigationState()
    }

    switchMainView("collection")
    switchCollectionType("search-results")
    clearSelected() // TODO: needed?
    this.setNavigationStateToSearch({ search: nextSearch, fresh })
    emitInterfaceEvent("afterNavigatedToSearch", nextSearch)
  }

  onAfterNavigatedToSettings = () => {
    const { clearSelected } = useSelection()
    const { switchMainView } = useMainView()
    const { openSettings } = useSettings()

    // if we have no prior file here, i.e. we just landed on the page, since if we were
    // doing an internal navigation we would have set the path file already (see
    // "navigateToFolder")

    this.setPriorNavigationState(this.state.value.current)
    this.setCurrentNavigationState({ navType: "settings" })
    this.clearNextNavigationState()

    switchMainView("settings")
    openSettings()
    clearSelected() // TODO: needed?
  }

  /**
   * When we receive file properties, we need to place them in the navigation path. If these
   * properties correspond to the "next" in the path, then we navigate to that path, as we
   * are retrieving the properties for a fresh navigation
   */

  onNavigationFileProperties = (event: FileEvent) => {
    switch (this.state.value.current?.navType) {
      case "collection-folder": {
        const file = new InterfaceFile(event)

        let root = this.currentRootFolder.value
        if (root && root.fileId === event.fileId)
          root.mergeWithNewer(file)
        else
          root = file

        // TODO: because this path handler lets us receive updates from any
        // kind of file properties event, we need to be very careful to reject
        // responses that don't have our properties after the merge
        if (!root.mimeType())
          return

        if (!root.isFolder())
          throw new Error("File is not a folder")

        // TODO: if this is your home/archive then redirect there
        return this.setNavigationStateRootFolder({
          root,
          sharedFolderHome: this.getSharedFolderHomeFromStateOrPath(root),
        })
      }
      case "preview-file": {
        let file = new InterfaceFile(event)

        // TODO: because this path handler lets us receive updates from any
        // kind of file properties event, we need to be very careful to reject
        // responses that don't have our properties
        // TODO: if this file is a folder or link, redirect to 404
        if (!file.mimeType())
          return
        if (file.isFolder() || file.isLink())
          throw new Error("File cannot be previewed")

        const preview = this.currentFilePreview.value
        if (preview && file.fileId === preview.fileId)
          file = preview.mergeWithNewer(file)

        const sharedFolderHome = this.getSharedFolderHomeFromStateOrPath(file)
        return this.setNavigationStateToFilePreview({ file, sharedFolderHome })
      }
    }

    // throw new Error("Invalid navigation state on properties response")
  }

  onNavigationPathProperties = (event: FileEvent) => {
    const { getFileProperties } = useFileSystem()

    const root = this.currentRootFolder.value || this.currentFilePreview.value
    if (root === undefined || root.path() === undefined)
      return
    for (const file of root.path() || [])
      if (file.fileId === event.fileId)
        return getFileProperties(root.fileId, InterfaceSelection.minimumPathSelection().inner())
  }

  onNavigationSearchProperties = (event: SearchEvent) => {
    switch (this.state.value.current?.navType) {
      case "collection-search": {
        const retrieved = new InterfaceSearch(event)
        let search = this.currentSearch.value
        const fresh = this.currentSearchIsFresh.value
        if (search && search.searchId() === retrieved.searchId())
          search.mergeWithNewer(retrieved)
        else
          search = retrieved
        return this.setNavigationStateToSearch({ search, fresh })
      }
    }
  }

  /**
   * This handler updates our various navigation path listeners when we navigate to a new
   * Page
   */

  updateNavigationPathListeners = () => {
    const { addEntityEventListener, removeAllEntityEventListeners } = useEvents()
    const { getFileProperties } = useFileSystem()

    // remove all the listeners for the prior state
    removeAllEntityEventListeners("fileProperties", this.onNavigationFileProperties)
    removeAllEntityEventListeners("fileProperties", this.onNavigationPathProperties)
    removeAllEntityEventListeners("searchProperties", this.onNavigationSearchProperties)

    if (this.state.value.current) {
      switch (this.state.value.current.navType) {
        case "collection-folder": {
          const newFile = new InterfaceFile(this.state.value.current.root)
          addEntityEventListener(
            "fileProperties",
            newFile.fileId,
            this.onNavigationFileProperties,
          )
          newFile.path()?.map(file => addEntityEventListener(
            "fileProperties",
            file.fileId,
            this.onNavigationPathProperties,
          ))

          getFileProperties(newFile.fileId, InterfaceSelection.minimumPathSelection().inner())
          break
        }
        case "preview-file": {
          const newFile = new InterfaceFile(this.state.value.current.file)
          addEntityEventListener(
            "fileProperties",
            newFile.fileId,
            this.onNavigationFileProperties,
          )
          newFile.path()?.map(file => addEntityEventListener(
            "fileProperties",
            file.fileId,
            this.onNavigationPathProperties,
          ))

          getFileProperties(newFile.fileId, InterfaceSelection.minimumPathSelection().inner())
          break
        }
        case "collection-search": {
          const newSearch = new InterfaceSearch(this.state.value.current.search)
          addEntityEventListener(
            "searchProperties",
            newSearch.searchId(),
            this.onNavigationSearchProperties,
          )
          break
        }
        case "conversation": break
        case "settings": break
      }
    }
  }

  /**
   * This handler basically logs out the user or redirects them to the home if there's an
   * invalid request when navigating to a file
   */
  onFreshFileProperties = async (response: FilePropertiesResponseDatagram) => {
    const { removeInterfaceEventListenerByRequestId } = useEvents()
    const { notifyError } = useAlerts()
    const { isAuthenticated } = useCache()

    removeInterfaceEventListenerByRequestId("filePropertiesResponse", response.requestId)

    if (response.error) {
      this.error("Received a navigation error", response)
      notifyError("Cannot access that file", response.error.details)
      if (isAuthenticated())
        return await this.navigateToSelfHome()
      else {
        return await this.navigateToLoginScreenAndClearCredentials()
      }
    }
  }

  authenticateFreshConversation = async (conversationId: string) => {
    const { notifyError } = useAlerts()
    const { isAuthenticated } = useCache()

    // retrieve conversation properties and if we error, redirect to home or login

    const conversation = InterfaceConversation.blank(conversationId)

    try {
      return await conversation.getProperties().authoritative
    }
    catch (error) {
      const datagramError = error as DatagramError
      this.error("Failed to authenticate conversation", datagramError)
      notifyError("Cannot access that conversation", datagramError.details)
      if (isAuthenticated())
        await this.navigateToSelfHome()
      else {
        await this.navigateToLoginScreenAndClearCredentials()
      }
    }
  }

  /**
   * This handler basically logs out the user or redirects them to the home if there's an
   * invalid request when navigating to a search
   */
  onFreshSearchList = async (response: SearchListPageResponseDatagram) => {
    const { removeInterfaceEventListenerByRequestId } = useEvents()
    const { notifyError } = useAlerts()
    const { isAuthenticated } = useCache()

    // We only care about the first response, which will tell us if there was an auth
    // error.
    removeInterfaceEventListenerByRequestId("searchListPageResponse", response.requestId)

    if (response.error) {
      this.error("Received a navigation error", response)
      notifyError("Cannot access that search", response.error.details)
      if (isAuthenticated())
        return await this.navigateToSelfHome()
      else {
        return await this.navigateToLoginScreenAndClearCredentials()
      }
    }
  }
}

const navigation = new Navigation("navigation", theme.colors.violet.hex)
export default function () {
  return navigation.use()
}
